import { Spreadsheet } from '@/components/spreadsheet/spreadsheet';
import { dateService } from '@/services';
import * as _ from 'lodash';
import * as moment from 'moment';

export class DapSpreadsheetByMarkedArea extends Spreadsheet {
  name: string = `total_marked_area`;

  mount(dataSource: any[]) {
    const initialValue = 9;
    const totalMonths = dataSource[0].dapValuesFound.length;

    const sectionColumns: any = {
      main: [
        { key: 'markedAreaName', text: 'MARKED AREA', textAlign: 'center' },
        { key: 'aov', text: 'AOV', textAlign: 'center' }
      ],
      industryRegistration: [
        { key: 'industryRetailRegistration', text: 'INDUSTRY RETAIL REGISTRATION', format: '#,0' },
        { key: 'industryRegistrationInSegment', text: 'INDUSTRY RETAIL REGISTRATION IN SEGMENT', format: '#,0' },
        { key: 'percentSegmentInIndustryRetail', text: '% SEGMENT IN INDUSTRY RETAIL', format: '#0.0%' }
      ],
      clientRegistration: [
        { key: 'clientRetailRegistrationInSegment', text: 'TOYOTA RETAIL REGISTRATION IN SEGMENT', format: '#,0' },
        { key: 'clientMarketShareInSegment', text: 'MKT-SHARE TOYOTA IN SEGMENT', format: '#0.0%' }
      ],
      productSales: [
        { key: 'productSales3s', text: '3S', format: '#,0' },
        { key: 'productSales2s', text: '2S', format: '#,0' }
      ],
      dapValuesFound: [
        ..._.map(dataSource[0].dapValuesFound, () => ({ key: 'value', text: 'SALES GOAL', format: '#,0' }))
      ],
      dapMarketShareForecast: [
        ..._.map(dataSource[0].dapMarketShareForecast, () => ({ key: 'value', text: 'SALES GOAL', format: '#0.00%' }))
      ]
    };

    const sectionColumnsDateTime: any = {
      main: [{ key: 'markedAreaName', text: '' }],
      industryRegistration: [
        {
          key: 'industryRetailRegistration',
          text: `${dateService
            .parseDateToString(dataSource[0].initialHistoryPeriod, 'MMM/YY')
            .toUpperCase()} - ${dateService
            .parseDateToString(dataSource[0].finalHistoryPeriod, 'MMM/YY')
            .toUpperCase()}`
        }
      ],
      dapValuesFound: [
        ..._(dataSource[0].dapValuesFound)
          .map((d: any, i: number) => ({
            key: 'year',
            text: String(d.year),
            index: initialValue + i
          }))
          .uniqBy((d: any) => d.text)
          .value()
      ],
      dapMarketShareForecast: [
        ..._(dataSource[0].dapMarketShareForecast)
          .map((d: any, i: number) => ({
            key: 'year',
            text: String(d.year),
            index: initialValue + (totalMonths || 1) + i
          }))
          .uniqBy((d: any) => d.text)
          .value()
      ]
    };

    const sectionColumnsDescription: any = {
      main: [{ key: 'markedAreaName', text: '' }],
      industryRegistration: [{ key: 'industryRetailRegistration', text: '' }],
      dapValuesFound: [
        ..._.map(dataSource[0].dapValuesFound, d => ({
          key: 'month',
          text: moment()
            .startOf('year')
            .add(d.month - 1, 'months')
            .format('MMM')
            .toUpperCase()
        }))
      ],
      dapMarketShareForecast: [
        ..._.map(dataSource[0].dapMarketShareForecast, d => ({
          key: 'month',
          text: moment()
            .startOf('year')
            .add(d.month - 1, 'months')
            .format('MMM')
            .toUpperCase()
        }))
      ]
    };

    const sections: Array<any> = [
      { key: 'main', text: '', index: 0 },
      { key: 'industryRegistration', text: 'INDUSTRY REGISTRATION', index: 2 },
      { key: 'clientRegistration', text: 'TOYOTA REGISTRATION', index: 5 },
      { key: 'productSales', text: 'PRODUCT SALES', index: 7 },
      { key: 'dapValuesFound', text: 'MONTHLY ANNUAL PLAN', index: initialValue },
      {
        key: 'dapMarketShareForecast',
        text: 'MARKET SHARE FORECAST',
        index: initialValue + (totalMonths || 1)
      }
    ];

    const baseStyle: any = {
      textAlign: 'left',
      fontSize: 9,
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseHeaderStyle: any = {
      color: '#212121',
      fontSize: 9,
      bold: true,
      textAlign: 'center',
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseColorStyle: any = {
      main: '#bdbdbd',
      industryRegistration: '#90a4ae',
      clientRegistration: '#e57373',
      productSales: '#90a4ae',
      referenceMktShare: '#e57373',
      dnc: '#90a4ae',
      dapMarketShareForecast: '#81c784',
      dapValuesFound: '#90a4ae',
      grey: '#d1d1d1',
      greyDarken: '#c1c1c1'
    };

    const baseBorderColorStyle: any = {
      main: '#e0e0e0',
      industryRegistration: '#cfd8dc',
      clientRegistration: '#ffcdd2',
      productSales: '#cfd8dc',
      referenceMktShare: '#ffcdd2',
      dnc: '#cfd8dc',
      dapMarketShareForecast: '#c8e6c9',
      dapValuesFound: '#cfd8dc'
    };

    const baseBackgroundColorStyle: any = {
      main: '#fafafa',
      industryRegistration: '#f9fafb',
      clientRegistration: '#fff5f6',
      productSales: '#f9fafb',
      referenceMktShare: '#fff5f6',
      dnc: '#f9fafb',
      dapMarketShareForecast: '#f4f9f5',
      dapValuesFound: '#f9fafb'
    };

    const headerStyle = _.reduce(
      baseColorStyle,
      (acc: any, val: string, key: string) => {
        acc[key] = _.assign({}, baseHeaderStyle, { background: baseColorStyle[key] });
        return acc;
      },
      {}
    );

    const getCellStyle = (key: any) => {
      return _.assign({}, baseStyle, {
        textAlign: 'center',
        background: baseBackgroundColorStyle[key],
        borderLeft: { color: baseBorderColorStyle[key], size: 1 },
        borderRight: { color: baseBorderColorStyle[key], size: 1 },
        borderTop: { color: baseBorderColorStyle[key], size: 1 },
        borderBottom: { color: baseBorderColorStyle[key], size: 1 }
      });
    };

    const dataSourceGroup = _(dataSource)
      .groupBy(d => d.familyName)
      .value();

    this.name = `dap_${_.map(dataSourceGroup, (val: any, key: string) => key.toLowerCase()).join('_')}_${this.name}`;

    this.workbookOptions = {
      sheets: _.map(
        dataSourceGroup,
        (data: any, name: string) =>
          ({
            name: `TOTAL MARKED AREA ${name}`.toUpperCase(),
            frozenColumns: 2,
            frozenRows: 4,
            filter: {
              ref: `A4:${this.columnIndex[initialValue - 1 + totalMonths * 2]}${data.length + 4}`,
              columns: []
            },
            rows: [
              {
                cells: [
                  ..._.map(sections, (section: any) =>
                    _.assign({}, headerStyle[section.key], {
                      value: section.text,
                      index: section.index,
                      borderLeft: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderRight: { color: baseBorderColorStyle[section.key], size: 1 }
                    })
                  )
                ]
              },
              {
                cells: [
                  ..._(sectionColumnsDateTime)
                    .map((columns: any, key: any) =>
                      _.map(columns, (cell: any) =>
                        _.assign({}, getCellStyle(cell.color || key), {
                          value: cell.text,
                          bold: true,
                          index: cell.index,
                          background: baseColorStyle.grey,
                          borderLeft: { color: baseBorderColorStyle.main, size: 1 },
                          borderRight: { color: baseBorderColorStyle.main, size: 1 },
                          borderTop: { color: baseBorderColorStyle.main, size: 1 },
                          borderBottom: { color: baseBorderColorStyle.main, size: 1 }
                        })
                      )
                    )
                    .flatten()
                    .value()
                ]
              },
              {
                cells: [
                  ..._(sectionColumnsDescription)
                    .map((columns: any, key: any) =>
                      _.map(columns, (cell: any) =>
                        _.assign({}, getCellStyle(cell.color || key), {
                          value: cell.text,
                          bold: true,
                          background: baseColorStyle.greyDarken,
                          borderLeft: { color: baseBorderColorStyle.main, size: 1 },
                          borderRight: { color: baseBorderColorStyle.main, size: 1 },
                          borderTop: { color: baseBorderColorStyle.main, size: 1 },
                          borderBottom: { color: baseBorderColorStyle.main, size: 1 }
                        })
                      )
                    )
                    .flatten()
                    .value()
                ]
              },
              {
                height: 80,
                cells: [
                  ..._(sectionColumns)
                    .map((columns: any, key: any) =>
                      _.map(columns, (cell: any) =>
                        _.assign({}, headerStyle[cell.color || key], {
                          value: cell.text,
                          wrap: true,
                          borderLeft: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                          borderRight: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                          borderTop: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                          borderBottom: { color: baseBorderColorStyle[cell.color || key], size: 1 }
                        })
                      )
                    )
                    .flatten()
                    .value()
                ]
              },
              ..._.map(data, d => {
                return {
                  cells: [
                    ..._(sectionColumns)
                      .map((columns: any, key: any) =>
                        _.map(columns, (cell: any, index: number) => {
                          if (key === 'dapMarketShareForecast' || key === 'dapValuesFound') {
                            return _.assign({}, getCellStyle(cell.color || key), {
                              value: d[key][index][cell.key],
                              format: cell.format
                            });
                          }
                          return _.assign({}, getCellStyle(cell.color || key), {
                            value: d[cell.key],
                            format: cell.format
                          });
                        })
                      )
                      .flatten()
                      .value()
                  ]
                };
              })
            ],
            columns: [
              {
                index: 0,
                width: 150
              },
              {
                index: 1,
                width: 150
              },
              ..._.map(_.range(2, 50), index => ({
                index: index,
                width: 75
              }))
            ],
            showGridLines: false,
            mergedCells: [
              `A1:${this.columnIndex[sectionColumns.main.length - 1]}1`,
              `A2:${this.columnIndex[sectionColumns.main.length - 1]}2`,
              `A3:${this.columnIndex[sectionColumns.main.length - 1]}3`,
              `${this.columnIndex[sections[1].index]}2:${this.columnIndex[sections[4].index - 1]}2`,
              `${this.columnIndex[sections[1].index]}3:${this.columnIndex[sections[4].index - 1]}3`,
              `${this.columnIndex[sections[1].index]}1:${this.columnIndex[sections[2].index - 1]}1`,
              `${this.columnIndex[sections[2].index]}1:${this.columnIndex[sections[3].index - 1]}1`,
              `${this.columnIndex[sections[3].index]}1:${this.columnIndex[sections[4].index - 1]}1`,
              `${this.columnIndex[sections[4].index]}1:${this.columnIndex[sections[5].index - 1]}1`,
              `${this.columnIndex[sections[5].index]}1:${this.columnIndex[sections[5].index + totalMonths - 1]}1`,
              ..._.map(
                sectionColumnsDateTime.dapValuesFound,
                (c: any) =>
                  `${this.columnIndex[c.index]}2:${
                    this.columnIndex[
                      c.index -
                        1 +
                        (_(dataSource[0].dapValuesFound)
                          .groupBy((d: any) => d.year)
                          .find((val: any, key: string) => +key === +c.text) as any).length
                    ]
                  }2`
              ),
              ..._.map(
                sectionColumnsDateTime.dapMarketShareForecast,
                (c: any) =>
                  `${this.columnIndex[c.index]}2:${
                    this.columnIndex[
                      c.index -
                        1 +
                        (_(dataSource[0].dapMarketShareForecast)
                          .groupBy((d: any) => d.year)
                          .find((val: any, key: string) => +key === +c.text) as any).length
                    ]
                  }2`
              )
            ]
          } as any)
      )
    };

    return this;
  }
}
